/**
 * Can key echo be routed to a private output — an earbud — while the voice stays on the speaker? (3.6)
 *
 * 3.6 asks for it "where the platform allows", and its acceptance names the other outcome: "if output routing is
 * not possible in Safari, document the limitation and keep echo at low volume."
 *
 * **Feature-detected rather than assumed**, because the answer is a fact about a browser we do not control and
 * one that could change. What it detects is the only API the web has for the job: `setSinkId`, on either
 * `AudioContext` or an `HTMLMediaElement`. Without it, a page cannot choose where its audio goes at all — the
 * device decides, and the device sends everything to whatever is connected.
 *
 * The consequence, for whoever reads this next: on a platform without it, key echo and the person's voice come
 * out of the same place. With an earbud connected, the *partner* hears neither; without one, the partner hears
 * every letter. There is no arrangement in between, which is why `keyEchoVolume` exists and defaults low.
 *
 * See `docs/private-output.md` for what a caregiver can actually do about it.
 */

export interface PrivateOutputSupport {
  /** `AudioContext.setSinkId` — the one that would let the echo be routed independently. */
  audioContext: boolean;
  /** `HTMLMediaElement.setSinkId` — a fallback route, if echo were played through an element. */
  mediaElement: boolean;
  /** Either of the above. False on Safari at the time of writing. */
  available: boolean;
}

export function detectPrivateOutput(): PrivateOutputSupport {
  const audioContext =
    typeof AudioContext !== 'undefined' &&
    typeof (AudioContext.prototype as { setSinkId?: unknown }).setSinkId === 'function';

  const mediaElement =
    typeof HTMLMediaElement !== 'undefined' &&
    typeof (HTMLMediaElement.prototype as { setSinkId?: unknown }).setSinkId === 'function';

  return { audioContext, mediaElement, available: audioContext || mediaElement };
}

/**
 * Log the answer once at startup, next to the `navigator.audioSession` detection.
 *
 * Recorded rather than acted on: there is nothing to do differently, and a device that quietly assumed the
 * limitation would leave the next person to rediscover it. The line in the console is how the device itself
 * reports what it can and cannot do.
 */
export function reportPrivateOutput(): void {
  const support = detectPrivateOutput();
  if (support.available) {
    console.info('[audio] private output routing is available', support);
    return;
  }
  console.warn(
    '[audio] no private output routing (setSinkId absent) — key echo shares the speaker with the voice; ' +
      'keyEchoVolume is the only lever. See docs/private-output.md',
  );
}
